import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { motion } from "motion/react";
import { ArrowLeft } from "lucide-react";
import { useStaticTranslation } from "@/lib/staticTranslations";

interface Category {
  id: string;
  name: string;
  image: string;
  link: string;
}

interface CategoryGridProps {
  title: string;
  categories: Category[];
}

const CategoryGrid = ({ title, categories }: CategoryGridProps) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const { t } = useStaticTranslation("header");
  
  // Take first 6 categories for mobile/tablet (2 per row = 3 rows), 5 for desktop bento
  const displayCategories = categories.slice(0, 6);
  
  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className="py-20 px-4 sm:px-[45px] lg:px-[100px] bg-gradient-to-b from-background via-primary/[0.03] to-background relative overflow-hidden"
    >
      {/* Decorative background elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-primary/[0.02] rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-primary/[0.02] rounded-full blur-3xl pointer-events-none"></div>
      
      <div className="container mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm text-primary/60 tracking-[0.3em] uppercase mb-4" style={{ fontWeight: 300 }}>
            {t("ourCollection", "הקולקציה שלנו")}
          </p>
          <h2 className="text-4xl mb-4 tracking-wide" style={{ fontWeight: 700 }}>
            {title}
          </h2>
          <div className="flex items-center justify-center gap-2">
            <div className="w-20 h-px bg-gradient-to-r from-transparent to-primary/40"></div>
            <div className="w-1.5 h-1.5 bg-primary/60 rotate-45"></div>
            <div className="w-20 h-px bg-gradient-to-l from-transparent to-primary/40"></div>
          </div>
        </motion.div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-3 lg:grid-rows-2">
          {/* Large featured category - spans 2 cols and 2 rows on desktop */}
          {displayCategories[0] && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0 }}
              className="col-span-1 lg:col-span-2 lg:row-span-2"
            >
              <CategoryCard category={displayCategories[0]} className="h-[180px] md:h-[220px] lg:h-[380px]" isLarge />
            </motion.div>
          )}
          
          {/* Top right category */}
          {displayCategories[1] && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <CategoryCard category={displayCategories[1]} className="h-[180px] md:h-[220px] lg:h-[180px]" />
            </motion.div>
          )}
          
          {/* Bottom right category */}
          {displayCategories[2] && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <CategoryCard category={displayCategories[2]} className="h-[180px] md:h-[220px] lg:h-[180px]" />
            </motion.div>
          )}
          
          {/* Bottom row */}
          {displayCategories[3] && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <CategoryCard category={displayCategories[3]} className="h-[180px] md:h-[220px] lg:h-[220px]" />
            </motion.div>
          )}
          
          {displayCategories[4] && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="lg:hidden"
            >
              <CategoryCard category={displayCategories[4]} className="h-[180px] md:h-[220px]" />
            </motion.div>
          )}
          
          {displayCategories[5] && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="lg:hidden"
            >
              <CategoryCard category={displayCategories[5]} className="h-[180px] md:h-[220px]" />
            </motion.div>
          )}
          
          {/* Desktop only - 5th category spans 2 cols */}
          {displayCategories[4] && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="hidden lg:block lg:col-span-2"
            >
              <CategoryCard category={displayCategories[4]} className="h-[220px]" />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

interface CategoryCardProps {
  category: Category;
  isLarge?: boolean;
  className?: string;
}

const CategoryCard = ({ category, isLarge, className }: CategoryCardProps) => {
  return (
    <Link
      to={category.link}
      className={`group relative overflow-hidden block bg-muted/50 transition-all duration-500 ${className || ''}`}
    >
      {/* Image */}
      <img
        src={category.image}
        alt={category.name}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      
      {/* Gradient Overlay - darker for better text visibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
      
      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-end p-6">
        <div className="text-center transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
          <h3 
            className={`text-white mb-2 tracking-wide text-lg font-light ${isLarge ? 'lg:text-2xl' : 'lg:text-xl'}`}
          >
            {category.name}
          </h3>
          
          {/* Decorative line */}
          <div className="w-0 h-px bg-white/60 mx-auto group-hover:w-12 transition-all duration-500 mb-3"></div>
          
          {/* View Button */}
          <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
            <span className="text-white/90 text-sm view-label" style={{ fontWeight: 300 }}>
              לצפייה
            </span>
            <ArrowLeft className="w-4 h-4 text-white/90 transform rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1 ltr:rotate-180 transition-transform duration-300" />
          </div>
        </div>
      </div>
      
      {/* Corner Accents - always visible */}
      <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-white/40 transition-all duration-500"></div>
      <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-white/40 transition-all duration-500"></div>
    </Link>
  );
};

export default CategoryGrid;
